/**
 * Textarea Component - Reusable textarea
 *
 * Variants:
 * - default: Standard textarea
 * - filled: Filled background
 * - outline: Outlined textarea
 *
 * Sizes:
 * - sm: Small
 * - md: Medium (default)
 * - lg: Large
 */

import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/shared/lib/utils';

const textareaVariants = cva(
  'flex min-h-[80px] w-full rounded-md border resize-y transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'border-neutral-300 bg-white focus-visible:border-primary-500 focus-visible:ring-primary-500',
        filled: 'border-transparent bg-neutral-100 focus-visible:border-primary-500 focus-visible:ring-primary-500 focus-visible:bg-white',
        outline: 'border-2 border-neutral-300 bg-transparent focus-visible:border-primary-500 focus-visible:ring-primary-500',
      },
      size: {
        sm: 'px-3 py-2 text-xs',
        md: 'px-3 py-2 text-sm',
        lg: 'px-4 py-3 text-base',
      },
      state: {
        default: '',
        error: 'border-error-500 focus-visible:border-error-500 focus-visible:ring-error-500',
        success: 'border-success-500 focus-visible:border-success-500 focus-visible:ring-success-500',
        warning: 'border-warning-500 focus-visible:border-warning-500 focus-visible:ring-warning-500',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
      state: 'default',
    },
  }
);

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    VariantProps<typeof textareaVariants> {
  label?: string;
  error?: string;
  helperText?: string;
  maxLength?: number;
  showCount?: boolean;
  containerClassName?: string;
}

/**
 * Textarea Component
 *
 * @example
 * ```tsx
 * <Textarea placeholder="Enter text" />
 * <Textarea label="Description" rows={4} />
 * <Textarea maxLength={500} showCount />
 * <Textarea error="Too long" />
 * ```
 */
const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, variant, size, state, label, error, helperText, maxLength, showCount = false, containerClassName, id, value, ...props }, ref) => {
    const inputId = id || `textarea-${Math.random().toString(36).substr(2, 9)}`;
    const errorMessage = error || (state === 'error' ? 'This field has an error' : '');
    const hasError = !!errorMessage;
    const currentLength = String(value || '').length;

    return (
      <div className={cn('w-full', containerClassName)}>
        {label && (
          <label
            htmlFor={inputId}
            className={cn(
              'mb-1.5 block text-sm font-medium leading-6',
              hasError ? 'text-error-500' : 'text-neutral-900'
            )}
          >
            {label}
          </label>
        )}

        <div className="relative">
          <textarea
            id={inputId}
            ref={ref}
            maxLength={maxLength}
            value={value}
            className={cn(textareaVariants({ variant, size, state: hasError ? 'error' : state }), className)}
            aria-invalid={hasError}
            aria-describedby={hasError ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined}
            {...props}
          />

          {showCount && maxLength && (
            <div className="absolute bottom-2 right-2 text-xs text-neutral-500">
              {currentLength}/{maxLength}
            </div>
          )}
        </div>

        {hasError && (
          <p id={`${inputId}-error`} className="mt-1.5 text-sm text-error-500">
            {errorMessage}
          </p>
        )}

        {helperText && !hasError && (
          <p id={`${inputId}-helper`} className="mt-1.5 text-sm text-neutral-500">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';

export default Textarea;
